import { useState } from 'react';
import {
  Flex,
  Heading,
  Button,
  Table,
  Dialog,
  TextField,
  Select,
  Callout,
  Badge,
  IconButton,
  Text,
  Box,
  Card,
  Code,
} from '@radix-ui/themes';
import {
  PlusIcon,
  TrashIcon,
  CopyIcon,
  ExclamationTriangleIcon,
  CheckIcon,
} from '@radix-ui/react-icons';
import { useGetMyApiKeysQuery } from '../../graphql/queries/GetMyApiKeys.generated';
import { useCreateApiKeyMutation } from '../../graphql/mutations/CreateApiKey.generated';
import { useRevokeApiKeyMutation } from '../../graphql/mutations/RevokeApiKey.generated';
import { ApiKeyType } from '../../types';
import { toast } from '../../utils/toast';
import DeleteConfirmDialog from '../../components/DeleteConfirmDialog';

export default function ApiKeysPage() {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedKeyId, setSelectedKeyId] = useState<string | null>(null);
  const [selectedKeyLabel, setSelectedKeyLabel] = useState<string>('');
  const [newKeyLabel, setNewKeyLabel] = useState('');
  const [newKeyType, setNewKeyType] = useState<ApiKeyType>(ApiKeyType.User);
  const [plaintextKey, setPlaintextKey] = useState<string | null>(null);
  const [showKeyDialog, setShowKeyDialog] = useState(false);
  const [copied, setCopied] = useState(false);

  const { data, loading, error } = useGetMyApiKeysQuery();
  const [createApiKey, { loading: creating }] = useCreateApiKeyMutation();
  const [revokeApiKey, { loading: revoking }] = useRevokeApiKeyMutation();

  const apiKeys = data?.myApiKeys || [];

  const handleCreateKey = async () => {
    if (!newKeyLabel.trim()) {
      toast.error('Please enter a label for the API key');
      return;
    }

    try {
      const { data } = await createApiKey({
        variables: {
          input: {
            label: newKeyLabel,
            keyType: newKeyType,
          },
        },
        refetchQueries: ['GetMyApiKeys'],
      });

      if (data?.createApiKey.plaintextKey) {
        setPlaintextKey(data.createApiKey.plaintextKey);
        setShowKeyDialog(true);
        setCreateDialogOpen(false);
        setNewKeyLabel('');
        setNewKeyType(ApiKeyType.User);
        toast.success('API key created successfully');
      }
    } catch (err) {
      console.error('Create API key failed:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'An unknown error occurred';
      toast.error(`Failed to create API key: ${errorMessage}`);
    }
  };

  const handleRevokeKey = async () => {
    if (!selectedKeyId) return;

    try {
      await revokeApiKey({
        variables: { keyId: selectedKeyId },
        refetchQueries: ['GetMyApiKeys'],
      });

      toast.success(`Successfully revoked API key "${selectedKeyLabel}"`);
      setDeleteDialogOpen(false);
      setSelectedKeyId(null);
      setSelectedKeyLabel('');
    } catch (err) {
      console.error('Revoke API key failed:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'An unknown error occurred';
      toast.error(`Failed to revoke API key: ${errorMessage}`);
    }
  };

  const handleCopyKey = async () => {
    if (!plaintextKey) return;

    try {
      await navigator.clipboard.writeText(plaintextKey);
      setCopied(true);
      toast.success('API key copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleOpenDeleteDialog = (keyId: string, label: string) => {
    setSelectedKeyId(keyId);
    setSelectedKeyLabel(label);
    setDeleteDialogOpen(true);
  };

  return (
    <Flex direction="column" gap="4">
      {/* Header */}
      <Flex justify="between" align="center">
        <Heading size="6">API Keys</Heading>
        <Button size="2" onClick={() => setCreateDialogOpen(true)}>
          <PlusIcon /> Create API Key
        </Button>
      </Flex>

      {/* Description */}
      <Text color="gray" size="2">
        API keys allow you to authenticate API requests. Keep your keys secure
        and never share them publicly.
      </Text>

      {/* Error State */}
      {error && (
        <Callout.Root color="red">
          <Callout.Icon>
            <ExclamationTriangleIcon />
          </Callout.Icon>
          <Callout.Text>Failed to load API keys. {error.message}</Callout.Text>
        </Callout.Root>
      )}

      {/* Loading State */}
      {loading && (
        <Flex justify="center" align="center" style={{ minHeight: '200px' }}>
          <Text>Loading API keys...</Text>
        </Flex>
      )}

      {/* Empty State */}
      {!loading && apiKeys.length === 0 && (
        <Card style={{ padding: '48px', textAlign: 'center' }}>
          <Flex direction="column" align="center" gap="4">
            <div style={{ fontSize: '48px' }}>🔑</div>
            <Heading size="5">No API Keys Yet</Heading>
            <Text color="gray" style={{ maxWidth: '400px' }}>
              Create an API key to authenticate requests to the API
            </Text>
            <Button onClick={() => setCreateDialogOpen(true)}>
              Create Your First API Key
            </Button>
          </Flex>
        </Card>
      )}

      {/* API Keys Table */}
      {!loading && apiKeys.length > 0 && (
        <Table.Root variant="surface">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Label</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Type</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Created</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Last Used</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell width="80px"></Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {apiKeys.map((key) => (
              <Table.Row key={key.id}>
                <Table.Cell>
                  <Text weight="medium">{key.label}</Text>
                </Table.Cell>
                <Table.Cell>
                  <Badge
                    color={key.keyType === ApiKeyType.Team ? 'blue' : 'gray'}
                  >
                    {key.keyType}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <Text size="2" color="gray">
                    {formatDate(key.createdAt)}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <Text size="2" color="gray">
                    {key.lastUsedAt ? formatDate(key.lastUsedAt) : 'Never'}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <IconButton
                    size="2"
                    variant="ghost"
                    color="red"
                    onClick={() => handleOpenDeleteDialog(key.id, key.label)}
                  >
                    <TrashIcon />
                  </IconButton>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}

      {/* Create API Key Dialog */}
      <Dialog.Root open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <Dialog.Content maxWidth="450px">
          <Dialog.Title>Create API Key</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            Create a new API key for authentication. The key will only be shown
            once.
          </Dialog.Description>

          <Flex direction="column" gap="3">
            <label>
              <Text as="div" size="2" mb="1" weight="bold">
                Label
              </Text>
              <TextField.Root
                placeholder="My API Key"
                value={newKeyLabel}
                onChange={(e) => setNewKeyLabel(e.target.value)}
              />
            </label>

            <label>
              <Text as="div" size="2" mb="1" weight="bold">
                Type
              </Text>
              <Select.Root
                value={newKeyType}
                onValueChange={(value) => setNewKeyType(value as ApiKeyType)}
              >
                <Select.Trigger style={{ width: '100%' }} />
                <Select.Content>
                  <Select.Item value={ApiKeyType.User}>User</Select.Item>
                  <Select.Item value={ApiKeyType.Team}>Team</Select.Item>
                </Select.Content>
              </Select.Root>
            </label>
          </Flex>

          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </Dialog.Close>
            <Button onClick={handleCreateKey} loading={creating}>
              Create Key
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {/* Show Plaintext Key Dialog */}
      <Dialog.Root open={showKeyDialog} onOpenChange={setShowKeyDialog}>
        <Dialog.Content maxWidth="550px">
          <Dialog.Title>API Key Created</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            <Callout.Root color="orange" mb="3">
              <Callout.Icon>
                <ExclamationTriangleIcon />
              </Callout.Icon>
              <Callout.Text>
                Make sure to copy your API key now. You won&apos;t be able to
                see it again!
              </Callout.Text>
            </Callout.Root>
          </Dialog.Description>

          <Box mb="4">
            <Text as="div" size="2" mb="2" weight="bold">
              Your API Key
            </Text>
            <Flex gap="2">
              <Code
                style={{
                  flex: 1,
                  padding: '12px',
                  fontSize: '14px',
                  wordBreak: 'break-all',
                  backgroundColor: 'var(--gray-3)',
                }}
              >
                {plaintextKey}
              </Code>
              <IconButton size="3" variant="soft" onClick={handleCopyKey}>
                {copied ? <CheckIcon /> : <CopyIcon />}
              </IconButton>
            </Flex>
          </Box>

          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button>Done</Button>
            </Dialog.Close>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Revoke API Key"
        description={`Are you sure you want to revoke "${selectedKeyLabel}"? This action cannot be undone and any applications using this key will stop working.`}
        onConfirm={handleRevokeKey}
        loading={revoking}
      />
    </Flex>
  );
}
